/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef } from 'preact/hooks';
import type { Ref, MutableRef } from 'preact/hooks';
import { useUser } from '../hooks/UNSAFE_useUser';
import { getValue, getDimensions } from './RatingUtils';

export type CommitDetail = {
  value: number;
};

export type InputDetail = {
  value?: number;
};

export const useEvents = (
  max: number,
  step: number,
  value: number,
  rootRef: Ref<HTMLElement>,
  dimensionsRef: MutableRef<{ width: number; x: number }>,
  isInteractive: boolean,
  onCommit?: (detail: CommitDetail) => void,
  onInput?: (detail: InputDetail) => void
) => {
  const currentInput = useRef<number>();
  const { direction } = useUser();

  if (!isInteractive) {
    return {};
  }

  const isRtl = direction === 'rtl';

  const handleInput = (inputValue: number | undefined) => {
    onInput?.({ value: inputValue } as InputDetail);
    currentInput.current = inputValue;
  };

  const cancelEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const pointerUpHandler = (event: PointerEvent) => {
    const val = getValue(event.pageX, max, step, dimensionsRef, isRtl);
    if (val !== -1) {
      onCommit?.({ value: val } as CommitDetail);
    }
  };

  // When the document or rating gauge parent scrolls, we need to update the dimensionRef.current.x.
  // Instead of updating it for every scroll event, recalculate the dims the first time mouse enters
  // so we get fresh dimension value.
  const pointerEnterHandler = () => {
    dimensionsRef.current = getDimensions(rootRef.current);
  };

  const hoverHandler = (event: PointerEvent) => {
    const val = getValue(event.pageX, max, step, dimensionsRef, isRtl);
    if (val !== -1 && val != currentInput.current) {
      handleInput(val);
    }
  };

  const blurHandler = () => {
    onCommit?.({ value: value } as CommitDetail);
  };

  const pointerLeaveHandler = () => {
    handleInput(undefined);
  };

  const keyDownHandler = (event: KeyboardEvent) => {
    const key = event.key;

    switch (key) {
      case 'Tab':
        return;
      case 'ArrowDown':
        handleInput(Math.max(0, value - step));
        break;
      case 'ArrowUp':
        handleInput(Math.min(max, value + step));
        break;
      case 'ArrowLeft': {
        const inputValue = isRtl ? Math.min(max, value + step) : Math.max(0, value - step);
        handleInput(inputValue);
        break;
      }
      case 'ArrowRight': {
        const inputValue = isRtl ? Math.max(0, value - step) : Math.min(max, value + step);
        handleInput(inputValue);
        break;
      }
    }
    cancelEvent(event);
  };

  const keyUpHandler = (event: KeyboardEvent) => {
    const key = event.key;

    switch (key) {
      case 'Enter':
        onCommit?.({ value: value } as CommitDetail);
        break;
      case 'Tab':
        handleInput(value);
        break;
      case 'Home':
        handleInput(0);
        break;
      case 'End':
        handleInput(max);
        break;
    }
    cancelEvent(event);
  };

  return {
    onPointerUp: pointerUpHandler,
    onBlur: blurHandler,
    onPointerMove: hoverHandler,
    onKeyUp: keyUpHandler,
    onKeyDown: keyDownHandler,
    onPointerLeave: pointerLeaveHandler,
    onPointerEnter: pointerEnterHandler
  };
};
